import { formatDate } from '@angular/common';
import { Component, LOCALE_ID, computed, inject, input, output, signal } from '@angular/core';

import { addCalendarDays, fromDayKey, isoWeek, toDayKey } from '../../../helpers/calendar-day-helpers';
import {
  PlanningTone,
  TimelineBar,
  TimelineDragSource,
  TimelineDrop,
  TimelineRow,
  TimelineRowDay,
} from '../../../models';
import { TimelineDragService } from '../../../services/timeline-drag.service';

// A day column on the board.
interface TimelineDayColumn {
  index: number;
  dayName: string;
  dayNumber: string;
  isWeekend: boolean;
  isToday: boolean;
  isMonday: boolean;
}

// A week band above the day columns.
interface TimelineWeekBand {
  key: string;
  label: string;
  days: number;
}

// Where a drag would land on a row: the index among the row's bars, and the x position (in
// days) to draw the marker at.
interface TimelineDropTarget {
  rowId: string;
  index: number;
  position: number;
  startAt: number | null;
}

// The tooltip card for the bar under the pointer, in VIEWPORT coordinates (it is position:
// fixed, so it floats over the page rather than being clipped by the board's frame), and
// which way it opens so it never runs off the right or bottom of the window.
interface TimelineHover {
  bar: TimelineBar;
  x: number;
  y: number;
  flipX: boolean;
  flipY: boolean;
}

@Component({
  selector: 'app-timeline-board',
  templateUrl: './timeline-board.component.html',
  styleUrl: './timeline-board.component.scss',
})
// A Gantt-style board: one row per resource, days across, bars that can be dragged along a row,
// onto another row, or off to an <app-timeline-tray>. Production plan, 2026-10-26.
//
// It is deliberately DUMB. It draws what it is given and reports what the user did - a drop
// with the row and the place among that row's bars - and never moves a bar itself. The caller
// decides what a drop means (here: re-queue the job and reschedule) and hands back new rows.
// That keeps one board usable for any resource plan: looms, dye machines, people.
//
// Positions are in DAYS from startDate, so the caller also decides what a fraction of a day is.
export class TimelineBoardComponent {
  private readonly locale = inject(LOCALE_ID);
  protected readonly drag = inject(TimelineDragService);

  public readonly rows = input<TimelineRow[]>([]);
  // yyyy-MM-dd.
  public readonly startDate = input.required<string>();
  public readonly days = input(14);
  public readonly dayWidth = input(72);
  // Where "now" is, in days from startDate; null draws no marker.
  public readonly nowPosition = input<number | null>(null);
  public readonly rowHeader = input($localize`Resource`);
  public readonly emptyText = input($localize`Nothing to show.`);
  // Whether a bar can be dropped into empty space to start LATER than the bar before it ends.
  // Off, a row is a packed queue and a drop only changes the order.
  public readonly allowGaps = input(false);

  public readonly dropped = output<TimelineDrop>();
  public readonly barClicked = output<TimelineBar>();
  public readonly rowClicked = output<TimelineRow>();

  // The fixed width of the row titles; the scale scrolls beside them.
  protected readonly headWidth = 168;

  protected readonly dropTarget = signal<TimelineDropTarget | null>(null);
  protected readonly hover = signal<TimelineHover | null>(null);

  protected readonly trackWidth = computed(() => this.days() * this.dayWidth());

  protected readonly columns = computed<TimelineDayColumn[]>(() => {
    const start = fromDayKey(this.startDate());
    const today = toDayKey(new Date());

    return Array.from({ length: this.days() }, (_, index) => {
      const date = addCalendarDays(start, index);
      const day = date.getDay();

      return {
        index,
        dayName: formatDate(date, 'EEE', this.locale),
        dayNumber: formatDate(date, 'd', this.locale),
        isWeekend: day === 0 || day === 6,
        isToday: toDayKey(date) === today,
        isMonday: day === 1,
      };
    });
  });

  // Weeks run Monday to Sunday; the first and last may be part weeks.
  protected readonly weeks = computed<TimelineWeekBand[]>(() => {
    const start = fromDayKey(this.startDate());
    const bands: TimelineWeekBand[] = [];

    for (let index = 0; index < this.days(); index++) {
      const date = addCalendarDays(start, index);

      if (index === 0 || date.getDay() === 1) {
        const monday = addCalendarDays(date, -((date.getDay() + 6) % 7));
        bands.push({
          key: toDayKey(monday),
          label: $localize`w/c ${formatDate(monday, 'd MMM', this.locale)} · wk ${isoWeek(monday)}`,
          days: 0,
        });
      }

      bands[bands.length - 1].days++;
    }

    return bands;
  });

  // The bars that fall inside the visible days, sorted by start - what the row draws and what a
  // drop's index is counted against.
  protected visibleBars(row: TimelineRow): TimelineBar[] {
    return [...row.bars].filter((bar) => bar.end >= 0 && bar.start <= this.days()).sort((a, b) => a.start - b.start);
  }

  protected left(position: number): number {
    return Math.max(0, Math.min(this.days(), position)) * this.dayWidth();
  }

  // A bar is never narrower than a grab handle, so a zero-length job can still be picked up.
  protected width(bar: TimelineBar): number {
    return Math.max(10, this.left(bar.end) - this.left(bar.start));
  }

  protected loadPercent(day: TimelineRowDay): number {
    return day.available > 0 ? Math.min(100, (day.used / day.available) * 100) : 0;
  }

  protected dayTitle(day: TimelineRowDay): string {
    return day.available > 0
      ? $localize`Booked ${this.hours(day.used)} of ${this.hours(day.available)} hours`
      : $localize`Not working`;
  }

  protected toneClass(bar: TimelineBar): string {
    return `plan-tone-${bar.tone ?? PlanningTone.neutral}`;
  }

  protected onDragStart(event: DragEvent, bar: TimelineBar, row: TimelineRow) {
    if (bar.locked) {
      event.preventDefault();
      return;
    }

    this.hover.set(null);
    this.drag.start({ itemId: bar.id, source: TimelineDragSource.bar, fromRowId: row.id }, event);
  }

  protected onDragEnd() {
    this.drag.end();
    this.dropTarget.set(null);
  }

  protected onDragOver(event: DragEvent, row: TimelineRow, track: HTMLElement) {
    const item = this.drag.current();

    if (!item) {
      return;
    }

    // Accepting the drop is what lets it happen at all.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    this.dropTarget.set(this.targetFor(event, row, track, item.itemId));
  }

  protected onDragLeave(event: DragEvent, track: HTMLElement) {
    // dragleave fires on every child crossed; only leaving the track itself clears the marker.
    if (!track.contains(event.relatedTarget as Node)) {
      this.dropTarget.set(null);
    }
  }

  protected onDrop(event: DragEvent, row: TimelineRow, track: HTMLElement) {
    const item = this.drag.current();

    if (!item) {
      return;
    }

    event.preventDefault();
    const target = this.targetFor(event, row, track, item.itemId);

    this.dropTarget.set(null);
    this.drag.end();

    this.dropped.emit({
      itemId: item.itemId,
      source: item.source,
      fromRowId: item.fromRowId,
      toRowId: row.id,
      index: target.index,
      startAt: target.startAt,
    });
  }

  protected showHover(event: MouseEvent, bar: TimelineBar) {
    if (this.drag.current()) {
      return;
    }

    // Opens to the left / upwards when there is not room for the card to the right / below.
    this.hover.set({
      bar,
      x: event.clientX,
      y: event.clientY,
      flipX: event.clientX > window.innerWidth - 360,
      flipY: event.clientY > window.innerHeight - 340,
    });
  }

  protected hideHover() {
    this.hover.set(null);
  }

  // The bars on the row without the one being dragged; the drop goes before the first whose
  // middle is to the right of the pointer. Locked bars at the front (work already running)
  // cannot be jumped, so the index never goes in front of them.
  //
  // With gaps allowed, a drop on a DAY well clear of the bar in front is a request to start that
  // day (startAt); anywhere else packs the bar up against the one in front.
  private targetFor(event: DragEvent, row: TimelineRow, track: HTMLElement, itemId: string): TimelineDropTarget {
    const position = (event.clientX - track.getBoundingClientRect().left) / this.dayWidth();
    const others = row.bars.filter((bar) => bar.id !== itemId).sort((a, b) => a.start - b.start);

    const lockedAtFront = others.findIndex((bar) => !bar.locked);
    const firstMovable = lockedAtFront === -1 ? others.length : lockedAtFront;

    let index = others.findIndex((bar) => (bar.start + bar.end) / 2 > position);
    index = Math.max(index === -1 ? others.length : index, firstMovable);

    const previousEnd = index > 0 ? others[index - 1].end : Math.max(0, this.nowPosition() ?? 0);
    // A day of "snap" after the bar in front: a drop on the day it ends, or the day after, packs
    // the bar up against it - only a drop clearly out in empty space holds it to a day. Without
    // this, dropping just past a neighbour (to swap the two) read as "start then" and left a gap.
    const day = Math.floor(position);
    const startAt = this.allowGaps() && day >= Math.ceil(previousEnd) + 1 ? day : null;
    const marker = startAt ?? (index < others.length ? others[index].start : previousEnd);

    return { rowId: row.id, index, position: marker, startAt };
  }

  private hours(value: number): string {
    return (Math.round(value * 10) / 10).toString();
  }
}
